import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ClaudeRouteSettings, getClaudeRoute, setClaudeRoute } from '../../services/ModelsInUse';

/** 'bedrock:eu-central-1' → 'Bedrock Frankfurt (eu-central-1)' */
const REGION: Record<string, string> = {
  'eu-central-1': 'Frankfurt',
  'eu-north-1': 'Stockholm',
  'eu-west-3': 'Paris',
};
export const targetName = (t: string) =>
  t === 'anthropic'
    ? "Anthropic API (outside the EU)"
    : t.startsWith('bedrock:')
      ? `Bedrock ${REGION[t.slice(8)] ?? ''} (${t.slice(8)})`.replace('  ', ' ')
      : t;

const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Staff: where Claude calls go, in order. A call moves to the next platform
 * when one is throttled or down. Saved in the backend's app_config and used
 * by every server within 30 seconds; no deploy needed.
 */
const ClaudeRouteCard: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [settings, setSettings] = useState<ClaudeRouteSettings | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = (s: ClaudeRouteSettings) => {
    setSettings(s);
    setDraft(s.effective);
  };

  useEffect(() => {
    getClaudeRoute().then((res) => {
      if (res.success && res.data) load(res.data);
      else setFailed(true);
    });
  }, []);

  const eu = useMemo(() => {
    if (!settings) return [];
    const fromEnv = settings.environment.filter((t) => t !== 'anthropic');
    return fromEnv.length ? fromEnv : settings.available.filter((t) => t !== 'anthropic');
  }, [settings]);

  if (!settings) {
    return (
      <Card className="flex justify-center p-6 text-sm text-muted-foreground">
        {failed ? 'Could not load where Claude runs.' : <Loader2 className="h-5 w-5 animate-spin" />}
      </Card>
    );
  }

  const presets: { label: string; route: string[] }[] = [
    { label: 'EU only (Bedrock)', route: eu },
    { label: 'Bedrock, then Anthropic API', route: [...eu, 'anthropic'] },
    { label: 'Anthropic API, then Bedrock', route: ['anthropic', ...eu] },
  ];
  const unused = settings.available.filter((t) => !draft.includes(t));
  const changed = !same(draft, settings.effective);

  const move = (i: number, by: number) => {
    const next = [...draft];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    setDraft(next);
  };

  const save = async (route: string[] | null) => {
    setSaving(true);
    const res = await setClaudeRoute(route);
    setSaving(false);
    if (res.success && res.data) {
      load(res.data);
      onChanged?.();
      toast.success(
        route ? 'Saved. Every server uses it within 30 seconds.' : 'Back to the environment default.',
      );
    } else {
      toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || 'Could not save');
    }
  };

  return (
    <Card className="space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Where Claude runs</h2>
          <p className="text-sm text-muted-foreground">
            Tried in order: a call moves to the next platform when one is throttled or down. Changes apply to every
            server within 30 seconds, without a deploy.
          </p>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          {settings.override ? (
            <>
              Set here by {settings.updatedBy ?? 'staff'}
              {settings.updatedAt && <> on {new Date(settings.updatedAt).toLocaleString()}</>}
            </>
          ) : (
            'Environment default (LLM_CLAUDE_ROUTE)'
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <Button
            key={p.label}
            variant={same(draft, p.route) ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDraft(p.route)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <ol className="space-y-1">
        {draft.map((t, i) => (
          <li key={t} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
            <Checkbox checked onCheckedChange={() => setDraft(draft.filter((x) => x !== t))} aria-label={`Remove ${t}`} />
            <span className="w-5 tabular-nums text-muted-foreground">{i + 1}.</span>
            <span className="flex-1">{targetName(t)}</span>
            <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={i === draft.length - 1}
              onClick={() => move(i, 1)}
              aria-label="Move down"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
          </li>
        ))}
        {unused.map((t) => (
          <li key={t} className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
            <Checkbox checked={false} onCheckedChange={() => setDraft([...draft, t])} aria-label={`Add ${t}`} />
            <span className="w-5" />
            <span className="flex-1">{targetName(t)}</span>
          </li>
        ))}
      </ol>

      {draft.includes('anthropic') && (
        <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Anthropic's API processes prompts and documents outside the EU. Only include it once customers have agreed.
            {draft[0] === 'anthropic' && ' With it first, nearly all Claude calls go there.'}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={!changed || !draft.length || saving} onClick={() => save(draft)}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
        {changed && (
          <Button variant="ghost" disabled={saving} onClick={() => setDraft(settings.effective)}>
            Cancel
          </Button>
        )}
        {settings.override && (
          <Button variant="outline" disabled={saving} onClick={() => save(null)}>
            Use environment default ({settings.environment.map(targetName).join(', then ')})
          </Button>
        )}
      </div>
    </Card>
  );
};

export default ClaudeRouteCard;
